import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Snackbar,
  Alert,
  Grid,
  CircularProgress,
  TextField,
  InputAdornment
} from '@mui/material';
import {
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import { useDomain } from '../../context/DomainContext.js';
import { 
  getAppAuthorizedDomainById, 
  updateAppAuthorizedDomain, 
  validateAppAuthorizedDomainAdmin 
} from '../../api/appAuthorizedDomain.js';
import { getAppKeywordDomainById } from '../../api/appKeyWordsApi.js';

function GeneralSettings() {
  const { selectedDomain } = useDomain();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [keywords, setKeywords] = useState([]);
  
  // Settings states
  const [syncNamesEnabled, setSyncNamesEnabled] = useState(false);
  const [validateStatusEnabled, setValidateStatusEnabled] = useState(false);
  const [googleWorkspaceAdminEmail, setGoogleWorkspaceAdminEmail] = useState('');
  const [isAdminEmailValidated, setIsAdminEmailValidated] = useState(false);
  const [isEditingAdminEmail, setIsEditingAdminEmail] = useState(false);
  const [validatingEmail, setValidatingEmail] = useState(false);
  const [domainData, setDomainData] = useState(null);

  // Mappings
  const [firstNameKeyword, setFirstNameKeyword] = useState('');
  const [middleNameKeyword, setMiddleNameKeyword] = useState('');
  const [lastNameFatherKeyword, setLastNameFatherKeyword] = useState('');
  const [lastNameMotherKeyword, setLastNameMotherKeyword] = useState('');

  // Notification states
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  const showToast = (message, severity = 'success') => {
    setToast({ open: true, message, severity });
  };

  const handleCloseToast = () => {
    setToast({ ...toast, open: false });
  };

  // Fetch domain config & keywords
  useEffect(() => {
    if (!selectedDomain) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch domain details
        const domainRes = await getAppAuthorizedDomainById(selectedDomain);
        const domain = domainRes.data;
        setDomainData(domain);

        // Load configs or default to false/empty
        setSyncNamesEnabled(domain.sync_names_enabled || false);
        setValidateStatusEnabled(domain.validate_status_enabled || false);
        const adminEmailVal = domain.google_workspace_admin_email || '';
        setGoogleWorkspaceAdminEmail(adminEmailVal);
        setIsAdminEmailValidated(!!adminEmailVal);
        setIsEditingAdminEmail(!adminEmailVal);

        const mappings = domain.name_mappings || {};
        setFirstNameKeyword(mappings.firstNameKeywordId || '');
        setMiddleNameKeyword(mappings.middleNameKeywordId || '');
        setLastNameFatherKeyword(mappings.lastNameFatherKeywordId || '');
        setLastNameMotherKeyword(mappings.lastNameMotherKeywordId || '');

        // Fetch keywords
        const keywordsRes = await getAppKeywordDomainById(selectedDomain);
        setKeywords(keywordsRes.data || []);

      } catch (err) {
        console.error('Error fetching domain settings:', err);
        showToast('Error al cargar la configuración del dominio', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedDomain]);

  const handleValidateEmail = async () => {
    if (!googleWorkspaceAdminEmail) {
      showToast('Por favor ingrese un correo electrónico', 'warning');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(googleWorkspaceAdminEmail)) {
      showToast('El formato del correo ingresado no es válido', 'warning');
      return;
    }
    setValidatingEmail(true);
    try {
      const res = await validateAppAuthorizedDomainAdmin(selectedDomain, googleWorkspaceAdminEmail);
      if (res.data.success) {
        setIsAdminEmailValidated(true);
        setIsEditingAdminEmail(false);
        showToast('Correo verificado y delegación validada exitosamente', 'success');
      } else {
        setIsAdminEmailValidated(false);
        showToast(`Error al validar: ${res.data.error || 'Acceso denegado'}`, 'error');
      }
    } catch (err) {
      console.error('Error validating admin email:', err);
      const errorMsg = err.response?.data?.error || err.message || 'Error de conexión';
      setIsAdminEmailValidated(false);
      showToast(`Error al validar: ${errorMsg}`, 'error');
    } finally {
      setValidatingEmail(false);
    }
  };

  const handleUnlockEmail = () => {
    setIsEditingAdminEmail(true);
    setIsAdminEmailValidated(false);
  };

  const handleSave = async () => {
    if (!selectedDomain || !domainData) return;

    if ((syncNamesEnabled || validateStatusEnabled) && !isAdminEmailValidated) {
      showToast('Debe validar el correo del administrador antes de guardar si la sincronización o la validación están activas', 'warning');
      return;
    }

    // Validation: if sync is enabled, mapping is required
    if (syncNamesEnabled) {
      if (!firstNameKeyword && !middleNameKeyword && !lastNameFatherKeyword && !lastNameMotherKeyword) {
        showToast('Debe mapear al menos un concepto de nombre o apellido', 'warning');
        return;
      }
    }

    setSaving(true);
    try {
      const updatedConfig = {
        ...domainData,
        sync_names_enabled: syncNamesEnabled,
        validate_status_enabled: validateStatusEnabled,
        google_workspace_admin_email: googleWorkspaceAdminEmail,
        name_mappings: {
          firstNameKeywordId: firstNameKeyword,
          middleNameKeywordId: middleNameKeyword,
          lastNameFatherKeywordId: lastNameFatherKeyword,
          lastNameMotherKeywordId: lastNameMotherKeyword
        }
      };

      // Strip document ID before sending PUT request
      delete updatedConfig.id;

      await updateAppAuthorizedDomain(selectedDomain, updatedConfig);
      showToast('Configuración guardada exitosamente', 'success');
    } catch (err) {
      console.error('Error saving domain settings:', err);
      showToast('Error al guardar la configuración', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ padding: '24px', maxWidth: '800px', margin: '0 auto' }}>
      <Typography variant="h5" sx={{ fontWeight: 'bold', marginBottom: '24px' }}>
        Configuración de Dominio ({domainData?.domain})
      </Typography>

      {/* Administrador Delegado Card */}
      <Card sx={{ marginBottom: '24px', boxShadow: 2, borderRadius: 2 }}>
        <CardContent sx={{ padding: '24px' }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', marginBottom: '16px' }}>
            Credenciales de Administración (Google Workspace)
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ marginBottom: '16px' }}>
            Ingresa la cuenta de correo electrónico de un administrador del dominio para delegar permisos de acceso a las APIs de Workspace (Directory y Gmail API).
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="Correo Administrador para Delegación"
                variant="outlined"
                size="small"
                disabled={!isEditingAdminEmail}
                value={googleWorkspaceAdminEmail}
                onChange={(e) => setGoogleWorkspaceAdminEmail(e.target.value)}
                placeholder="ejemplo@dominio.com"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {isAdminEmailValidated ? (
                        <CheckCircleIcon color="success" />
                      ) : (
                        <LockIcon color="disabled" />
                      )}
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              {isEditingAdminEmail ? (
                <Button
                  fullWidth
                  variant="outlined"
                  color="primary"
                  onClick={handleValidateEmail}
                  disabled={validatingEmail}
                  sx={{ textTransform: 'none', fontWeight: 'bold' }}
                >
                  {validatingEmail ? 'Validando...' : 'Validar'}
                </Button>
              ) : (
                <Button
                  fullWidth
                  variant="outlined"
                  color="secondary"
                  startIcon={<EditIcon />}
                  onClick={handleUnlockEmail}
                  sx={{ textTransform: 'none', fontWeight: 'bold' }}
                >
                  Editar/Desbloquear
                </Button>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Sincronización de Nombres Card */}
      <Card sx={{ marginBottom: '24px', boxShadow: 2, borderRadius: 2 }}>
        <CardContent sx={{ padding: '24px' }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', marginBottom: '16px' }}>
            Sincronización de Nombres (Google Workspace)
          </Typography>
          
          <FormControlLabel
            control={
              <Switch
                checked={syncNamesEnabled}
                onChange={(e) => setSyncNamesEnabled(e.target.checked)}
                color="primary"
              />
            }
            label="Actualizar nombres y apellidos desde Google Workspace"
            sx={{ marginBottom: syncNamesEnabled ? '24px' : '0px' }}
          />

          {syncNamesEnabled && (
            <Box sx={{ marginTop: '16px' }}>
              <Typography variant="body2" color="textSecondary" sx={{ marginBottom: '20px' }}>
                Selecciona la palabra clave correspondiente en tu plantilla para cada uno de los siguientes conceptos:
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="first-name-label">Primer Nombre</InputLabel>
                    <Select
                      labelId="first-name-label"
                      value={firstNameKeyword}
                      label="Primer Nombre"
                      onChange={(e) => setFirstNameKeyword(e.target.value)}
                    >
                      <MenuItem value="">
                        <em>Ninguno</em>
                      </MenuItem>
                      {keywords.map((kw) => (
                        <MenuItem key={kw.id} value={kw.id}>
                          {kw.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="middle-name-label">Segundo Nombre</InputLabel>
                    <Select
                      labelId="middle-name-label"
                      value={middleNameKeyword}
                      label="Segundo Nombre"
                      onChange={(e) => setMiddleNameKeyword(e.target.value)}
                    >
                      <MenuItem value="">
                        <em>Ninguno</em>
                      </MenuItem>
                      {keywords.map((kw) => (
                        <MenuItem key={kw.id} value={kw.id}>
                          {kw.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="last-name-father-label">Apellido Paterno</InputLabel>
                    <Select
                      labelId="last-name-father-label"
                      value={lastNameFatherKeyword}
                      label="Apellido Paterno"
                      onChange={(e) => setLastNameFatherKeyword(e.target.value)}
                    >
                      <MenuItem value="">
                        <em>Ninguno</em>
                      </MenuItem>
                      {keywords.map((kw) => (
                        <MenuItem key={kw.id} value={kw.id}>
                          {kw.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="last-name-mother-label">Apellido Materno</InputLabel>
                    <Select
                      labelId="last-name-mother-label"
                      value={lastNameMotherKeyword}
                      label="Apellido Materno"
                      onChange={(e) => setLastNameMotherKeyword(e.target.value)}
                    >
                      <MenuItem value="">
                        <em>Ninguno</em>
                      </MenuItem>
                      {keywords.map((kw) => (
                        <MenuItem key={kw.id} value={kw.id}>
                          {kw.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Validación de Estado Card */}
      <Card sx={{ marginBottom: '24px', boxShadow: 2, borderRadius: 2 }}>
        <CardContent sx={{ padding: '24px' }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', marginBottom: '16px' }}>
            Validación de Cuentas (Google Workspace)
          </Typography>
          
          <FormControlLabel
            control={
              <Switch
                checked={validateStatusEnabled}
                onChange={(e) => setValidateStatusEnabled(e.target.checked)}
                color="primary"
              />
            }
            label="Validar estado de cuentas desde Google Workspace"
          />
          <Typography variant="body2" color="textSecondary" sx={{ marginTop: '8px', marginLeft: '38px' }}>
            Al habilitar este parámetro, el sistema consultará directamente a Google Workspace si la cuenta de correo se encuentra activa o suspendida antes de realizar operaciones.
          </Typography>
        </CardContent>
      </Card>

      {/* Save Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={saving || ((syncNamesEnabled || validateStatusEnabled) && !isAdminEmailValidated)}
          sx={{ minWidth: '120px', textTransform: 'none', fontWeight: 'bold' }}
        >
          {saving ? 'Guardando...' : 'Guardar'}
        </Button>
      </Box>

      {/* Toast Notification */}
      <Snackbar open={toast.open} autoHideDuration={6000} onClose={handleCloseToast}>
        <Alert onClose={handleCloseToast} severity={toast.severity} sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default GeneralSettings;
